import {Component} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs/operators';
import {SplashScreen} from '@capacitor/splash-screen';
import {PlatformService} from './services/core/platform.service';
import {ThemeService} from './services/core/theme.service';
import {registerLocaleData} from '@angular/common';
import localeDeDe from '@angular/common/locales/de';
import {AppVersionService} from './services/global/app-version.service';
import {register} from 'swiper/element/bundle';
import {StartUpService} from "./services/global/start-up.service";
import {AuthService} from "./services/global/auth.service";
import {AdminDeliverySimulatorService} from "./services/admin/admin-delivery-simulator.service";

register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent {

  private static readonly VISITOR_ID_STORAGE_KEY = 'kiwi-visitor-id';

  // pages of the administration: the side menu (split pane) is active
  adminActive = false;
  simulatorActive = false;

  constructor(
    private platformService: PlatformService,
    private appVersionService: AppVersionService,
    private startUpService: StartUpService,
    private authService: AuthService,
    private router: Router,
    private simulator: AdminDeliverySimulatorService,
    private themeService: ThemeService
  ) {
    this.simulatorActive = this.simulator.isActive();
    this.simulator.Changed.subscribe(() => this.simulatorActive = this.simulator.isActive());
    this.authService.Logout.subscribe(() => this.simulator.stop());
    this.initializeApp().then(() => {}).catch(e => console.log('error while init', e));
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      const url = event.urlAfterRedirects || '';
      this.adminActive = url === '/admin' || url.startsWith('/admin/') || url.startsWith('/admin?');
    });
  }

  openSimulator() {
    return this.router.navigate(['/admin', 'delivery-simulator']);
  }

  async initializeApp() {
    SplashScreen.show();
    await this.platformService.init();
    await this.initColors();
    registerLocaleData(localeDeDe);
    this.startUpService.init();
    await this.ensureVisitorIdAndGuestLogin();
    await this.checkAppVersion();
    setTimeout(() => SplashScreen.hide(), 800);
  }

  async checkAppVersion() {
    this.appVersionService.init();
    await this.appVersionService.checkVersion();
  }

  // dark mode (default) or light mode of the profile, also the color of the status bar
  async initColors() {
    await this.themeService.init();
  }

  private async ensureVisitorIdAndGuestLogin() {
    if (!this.platformService.isBrowser()) {
      return;
    }

    const existingId = localStorage.getItem(AppComponent.VISITOR_ID_STORAGE_KEY);
    if (existingId) {
      return;
    }

    localStorage.setItem(AppComponent.VISITOR_ID_STORAGE_KEY, this.generateVisitorId());
    this.applyFirstVisitDefaults();

    const isLoggedIn = await this.authService.isLoggedIn();
    if (!isLoggedIn) {
      try {
        await this.authService.loginAsGuest();
      } catch (error) {
        console.error('Guest login failed on first visit', error);
      }
    }
  }

  private generateVisitorId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    const randomPart = Math.random().toString(36).substring(2, 10);
    return `guest-${Date.now()}-${randomPart}`;
  }

  private applyFirstVisitDefaults(): void {
    const defaults: Record<string, string> = {
      'is-guest': 'true',
      'provider-type': 'kiwi',
      'kiwi-cart-synchronized': 'true',
      'show-appointments': 'true',
      'delivery-type': 'appointment',
      'provider-setting':'{"apple":true,"facebook":false,"google":true}'
    };

    Object.entries(defaults).forEach(([key, value]) => this.setLocalStorageIfMissing(key, value));
  }

  private setLocalStorageIfMissing(key: string, value: string): void {
    if (localStorage.getItem(key) === null) {
      localStorage.setItem(key, value);
    }
  }
}
